import { CalendarDays, Route } from 'lucide-react'
import { formatEventStart } from '@/lib/explore'
import { formatCourseLength } from '@/lib/watch/rules'
import { WatchSheet } from './WatchSheet'
import './watch.css'

/** A course of the event, as the viewer payload lists it. */
export interface EventDetailsCourse {
  id: string
  name?: string | null
  distance_km?: number | null
}

export interface EventDetailsSheetProps {
  open: boolean
  onOpenChange(open: boolean): void
  /** The event: its name (the sheet's title, in full), subtitle, start and description. */
  event: { name: string; subtitle?: string | null; description?: string | null; starts_at?: string | null }
  /** Its courses: the payload's routes, else its primary route. Each is named (when it has a name) with its length. */
  courses: readonly EventDetailsCourse[]
}

/**
 * The event's details on a phone, from the info line under the video when the event has one stage: its name in full
 * (the title), its subtitle, when it starts (the viewer's time, as /explore's cards say it), each course with its length,
 * and its description. A Play Drawer from the bottom (WatchSheet). Facts lead with a glyph and keep their name for
 * screen readers; whatever the event doesn't have is left out.
 */
export function EventDetailsSheet({ open, onOpenChange, event, courses }: EventDetailsSheetProps) {
  const starts = event.starts_at ? formatEventStart(event.starts_at) : ''
  const listed = courses
    .map((course) => ({ id: course.id, name: course.name?.trim() || null, length: formatCourseLength(course.distance_km) }))
    .filter((course) => course.name || course.length)
  const description = event.description?.trim()
  return (
    <WatchSheet
      open={open}
      onOpenChange={onOpenChange}
      title={event.name}
      description={event.subtitle?.trim() || undefined}
      className="watch-details-sheet"
    >
      {(starts || listed.length > 0) && (
        <dl className="watch-details">
          {starts && (
            <div className="watch-details__fact">
              <dt>
                <CalendarDays aria-hidden="true" />
                <span className="sr-only">Starts</span>
              </dt>
              <dd>
                <time dateTime={event.starts_at ?? undefined}>{starts}</time>
              </dd>
            </div>
          )}
          {listed.length > 0 && (
            <div className="watch-details__fact">
              <dt>
                <Route aria-hidden="true" />
                <span className="sr-only">{listed.length > 1 ? 'Courses' : 'Course'}</span>
              </dt>
              <dd>
                <ul className="watch-details__courses">
                  {listed.map((course) => (
                    <li key={course.id}>
                      {course.name && <span className="watch-details__course">{course.name}</span>}
                      {course.length && (
                        <span className="watch-details__length">
                          <span aria-hidden="true">{course.length.short}</span>
                          <span className="sr-only">{course.length.long}</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
        </dl>
      )}
      {description && <p className="watch-details__description">{description}</p>}
    </WatchSheet>
  )
}
